import { smartRound } from '@/utils/miscUtils';
import { unitToString } from '@/utils/unitUtils';
import PropTypes from 'prop-types';
import { ViewportPortal } from '@xyflow/react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useHover,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { useState } from 'react';

const skipLabels = ['scalar', '%/100'];

const PropertiesTooltip = ({ children, properties, label, isHidden = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasProperties = properties?.length > 0;

  const {
    refs: { setReference, setFloating },
    floatingStyles,
    context,
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift()],
  });

  const hover = useHover(context, { move: false });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);

  return (
    <div ref={setReference} {...getReferenceProps()} className="inline-block">
      {children}
      {hasProperties && isOpen && !isHidden && (
        <ViewportPortal>
          <div ref={setFloating} style={floatingStyles} {...getFloatingProps()} className="z-1000 mb-2 w-96 text-lg">
            <div className="rounded-lg border border-gray-300 bg-white p-3 shadow-lg">
              <div className="text-center font-semibold mb-2 text-black">{label}</div>
              <div className="space-y-1">
                {properties
                  .filter(property => property.value?.scalar !== 0)
                  .map((property, index) => (
                    <div key={property.name || index} className="text-black">
                      {property.label}: {smartRound(property.value?.scalar, 0, 3)}{' '}
                      {property.value?.unit && unitToString(property.value?.unit, skipLabels)}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </ViewportPortal>
      )}
    </div>
  );
};

PropertiesTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string,
  isHidden: PropTypes.bool,
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.shape({
        scalar: PropTypes.number,
        unit: PropTypes.shape({
          label: PropTypes.string,
        }),
      }),
    }),
  ),
};

export default PropertiesTooltip;
